<!-- app-subdomain-creators\src\Components\NewsfeedCreatorPendingTab.vue -->

<template>
  <div class="lg:w-[1175px] mx-auto pb-10 pt-5 min-h-[650px]">
    <div v-if="loading" class="py-8 text-center text-sm text-zinc-500">
      Loading pending posts...
    </div>

    <div v-else-if="errorMessage" class="py-8 text-center text-sm text-red-500">
      {{ errorMessage }}
    </div>

    <div v-else-if="!pendingPosts.length" class="flex flex-col items-center pt-10 pb-24 text-center">
      <div class="mb-4 flex items-center justify-center">
        <DocumentMagnifyingGlassIcon class="w-20 h-20 text-zinc-400" />
      </div>
      <p class="text-base font-semibold text-zinc-800 mb-1">No pending posts</p>
      <p class="text-sm text-zinc-500 max-w-md">
        Member posts waiting for moderation will appear here.
      </p>
    </div>

    <div v-else class="space-y-4 max-w-[700px]">
      <div
        v-for="post in pendingPosts"
        :key="post.id"
        class="relative rounded-xl border border-zinc-200 bg-white shadow-sm"
      >
        <div class="absolute top-3 right-4 z-10 group">
          <ScaleIcon
            class="w-6 h-6 text-[#2D8CFF]"
            @mouseenter="(e) => handlePendingBadgeTooltipEnter(post?.id, e)"
          />
          <p
            class="pointer-events-none absolute px-2 py-1.5 text-[11px] rounded-xl
                   bg-black/70 text-white whitespace-nowrap z-50 left-1/2 -translate-x-1/2 shadow-lg
                   opacity-0 translate-y-1
                   group-hover:opacity-100 group-hover:translate-y-0
                   transition-all duration-150"
            :class="pendingBadgeTooltipPosition[String(post?.id || '')] === 'below' ? 'top-7' : 'bottom-7'"
          >
            Awaiting your approval
          </p>
        </div>

        <div class="px-4 pt-3 pb-3 flex items-start gap-3">
          <img
            :src="getAvatar(post)"
            alt="Pending post author"
            class="h-10 w-10 rounded-full object-cover"
          />

          <div class="min-w-0 flex-1">
            <p class="text-sm font-semibold text-zinc-900 truncate">
              {{ getAuthorName(post) }}
            </p>
            <p class="text-xs text-zinc-500">
              Submitted {{ formatSubmittedAt(post) }}
            </p>
          </div>
        </div>

        <div
          v-if="String(post.content || post.body || '').trim().length > 0"
          :class="hasTextBackground(post) ? 'px-0 pb-3' : 'px-4 pb-1'"
        >
          <div
            :class="hasTextBackground(post) ? 'min-h-[472px] flex items-center justify-center text-center px-8 py-6 bg-cover bg-center bg-no-repeat' : ''"
            :style="resolveTextBackgroundStyle(post)"
          >
            <p
              :class="[
                bodyTextClass(post),
                'leading-snug whitespace-pre-line break-words',
                hasTextBackground(post) ? backgroundTextToneClass(post) : 'text-zinc-800',
              ]"
            >
              <span v-html="displayBodyHtml(post)"></span>
              <button
                v-if="isBodyLong(post) && !hasTextBackground(post)"
                type="button"
                :class="hasTextBackground(post) ? `inline text-sm font-semibold ml-1 hover:underline ${backgroundTextButtonClass(post)}` : 'inline text-sm font-semibold text-zinc-800 hover:text-zinc-900 ml-1 hover:underline'"
                @click.stop="toggleShowFullBody(post)"
              >
                {{ isPostExpanded(post) ? 'Show less' : 'See more' }}
              </button>
            </p>
          </div>
        </div>

        <div v-if="getDisplayMedia(post).length > 0" class="pb-3">
          <div
            v-if="getDisplayMedia(post).length === 1 && getDisplayMedia(post)[0].type === 'video'"
            class="w-full bg-black flex items-center justify-center overflow-hidden"
          >
            <div class="w-full">
              <NewsfeedInlineVideoPlayer
                :file-id="getDisplayMedia(post)[0].fileId ? String(getDisplayMedia(post)[0].fileId) : null"
                :feature-instance-id="resolvePendingFeatureInstanceId(post)"
                :src="getDisplayMedia(post)[0].videoSrc || null"
                :poster="getDisplayMedia(post)[0].thumbnailSrc || null"
                :autoplay="true"
                :muted="true"
                :max-height="680"
              />
            </div>
          </div>

          <div
            v-else-if="getDisplayMedia(post).length === 1"
            class="w-full bg-black flex items-center justify-center overflow-hidden"
          >
            <img
              :src="getDisplayMedia(post)[0].src"
              alt="Pending post media"
              class="max-h-[60vh] max-w-full w-auto object-contain"
            />
          </div>

          <div
            v-else
            class="grid grid-cols-2 gap-[3px] bg-white"
          >
            <div
              v-for="(item, idx) in getDisplayMedia(post)"
              :key="`${post.id}-${idx}`"
              class="bg-black min-h-[180px]"
            >
              <NewsfeedInlineVideoPlayer
                v-if="item.type === 'video'"
                :file-id="item.fileId ? String(item.fileId) : null"
                :feature-instance-id="resolvePendingFeatureInstanceId(post)"
                :src="item.videoSrc || null"
                :poster="item.thumbnailSrc || null"
                :autoplay="true"
                :muted="true"
                :max-height="360"
                :fill-container="true"
              />

              <img
                v-else
                :src="item.src"
                alt="Pending post media"
                class="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        <div class="px-4 pb-4 flex gap-2">
          <button
            type="button"
            class="flex-1 text-sm font-medium py-2 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            :disabled="actioningPostId === post.id"
            @click="approvePost(post)"
          >
            {{ actioningPostId === post.id ? 'Approving...' : 'Approve Post' }}
          </button>

          <button
            type="button"
            class="flex-1 text-sm font-medium py-2 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            :disabled="actioningPostId === post.id"
            @click="openRejectModal(post)"
          >
            Reject & Delete
          </button>
        </div>
      </div>
    </div>
  </div>

  <NewsfeedCreatorPendingRejectModal
    :is-open="rejectModalOpen"
    :is-submitting="actioningPostId === rejectTargetPostId && rejectModeActive"
    @cancel="closeRejectModal"
    @confirm="confirmReject"
  />
</template>

<script setup>
import { computed, defineExpose, defineProps, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useWebsocketStore } from '@/stores/websocketStore';
import { DocumentMagnifyingGlassIcon, ScaleIcon } from '@heroicons/vue/24/outline';
import NewsfeedCreatorPendingRejectModal from '@/Components/NewsfeedCreatorPendingRejectModal.vue';
import NewsfeedInlineVideoPlayer from '@/Components/NewsfeedInlineVideoPlayer.vue';
import { detectNewsfeedBackgroundTone, getNewsfeedBackgroundById, guessNewsfeedBackgroundTone } from '@/utils/newsfeedPostBackgrounds';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const app = getCurrentInstance();
const notify = app?.appContext?.config?.globalProperties?.$notify;
const websocketStore = useWebsocketStore();

let unsubscribeNewsfeedPostCreated = null;

const props = defineProps({
  featureInstanceId: {
    type: [Number, String],
    required: true,
  },
  postingAsName: {
    type: String,
    default: 'You',
  },
  postingAsAvatarUrl: {
    type: String,
    default: null,
  },
});

const loading = ref(false);
const errorMessage = ref('');
const pendingPosts = ref([]);
const actioningPostId = ref(null);

const rejectModalOpen = ref(false);
const rejectTargetPostId = ref(null);
const rejectModeActive = ref(false);
const expandedPostBodyById = ref({});
const textBackgroundToneByPostId = ref({});
const pendingBadgeTooltipPosition = ref({});

const defaultAvatar = new URL(
  '@/assets/media/images/default-avatar.png',
  import.meta.url
).href;

const featureInstanceIdNum = computed(() => {
  const id = Number(props.featureInstanceId);
  return Number.isFinite(id) && id > 0 ? id : 0;
});

function sortPending(list) {
  return [...list].sort((a, b) => {
    const aTs = new Date(a.createdAt || a.created_at || 0).getTime() || 0;
    const bTs = new Date(b.createdAt || b.created_at || 0).getTime() || 0;
    return bTs - aTs;
  });
}

function getAvatar(post) {
  const raw = String(post?.postedAsAvatarUrl || post?.authorAvatar || '').trim();
  if (!raw) return defaultAvatar;

  const match = raw.match(/\/api\/image\/([0-9a-fA-F-]{8,})/);
  if (!match?.[1]) return raw;
  return `${apiProxyUrl}/api/creator-newsfeed/image/${match[1]}`;
}

function getAuthorName(post) {
  const byPost = String(post?.postedAsName || '').trim();
  if (byPost) return byPost;
  const byAuthor = String(post?.authorName || '').trim();
  if (byAuthor) return byAuthor;
  return 'Member';
}

function formatSubmittedAt(post) {
  const raw = post?.createdAt || post?.created_at || null;
  if (!raw) return 'just now';
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return 'just now';
  return dt.toLocaleString();
}

function hasTextBackground(post) {
  const key = String(post?.backgroundColor || post?.background_color || '').trim();
  if (hasAnyAttachedMedia(post) || hasPoll(post)) return false;
  if (!key) return false;
  const mapped = getNewsfeedBackgroundById(key);
  if (mapped?.backgroundUrl) return true;
  return key.startsWith('#') || key.startsWith('rgb');
}

function resolveTextBackgroundStyle(post) {
  const value = String(post?.backgroundColor || post?.background_color || '').trim();
  if (!value) return {};

  const mapped = getNewsfeedBackgroundById(value);
  if (mapped?.backgroundUrl) {
    return {
      backgroundImage: `url(${mapped.backgroundUrl})`,
      backgroundSize: '100% 100%',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      color: '#fff',
    };
  }

  if (value.startsWith('#') || value.startsWith('rgb')) {
    return { backgroundColor: value, color: '#fff' };
  }

  return {};
}

function hasPoll(post) {
  return !!(
    post?.poll ||
    post?.pollData ||
    post?.poll_data ||
    post?.pollOptions ||
    post?.poll_options ||
    post?.hasPoll
  );
}

function hasAnyAttachedMedia(post) {
  const atts = Array.isArray(post?.attachments) ? post.attachments : [];
  const coverMediaFileId = post?.coverMediaFileId || post?.cover_media_file_id || null;
  return atts.length > 0 || !!coverMediaFileId;
}

function getPostBody(post) {
  return String(post?.body || post?.content || '');
}

function bodyTextClass(post) {
  const body = getPostBody(post);
  if (body.length > 0 && body.length < 85) return 'text-[22px]';
  return 'text-[15px]';
}

function getPostIdKey(post) {
  return String(post?.id || '');
}

function isPostExpanded(post) {
  const key = getPostIdKey(post);
  if (!key) return false;
  return !!expandedPostBodyById.value[key];
}

function toggleShowFullBody(post) {
  const key = getPostIdKey(post);
  if (!key) return;
  expandedPostBodyById.value = {
    ...expandedPostBodyById.value,
    [key]: !expandedPostBodyById.value[key],
  };
}

const PREVIEW_MAX_LINES = 2;
const PREVIEW_MAX_CHARS = 140;

function bodyPreviewText(post) {
  const body = getPostBody(post);
  if (!body) return '';

  const lines = body.split(/\r?\n/);
  let result = '';
  let linesUsed = 0;

  for (let i = 0; i < lines.length && linesUsed < PREVIEW_MAX_LINES; i++) {
    const line = lines[i];
    const prefix = linesUsed === 0 ? '' : '\n';
    const projectedLength = result.length + prefix.length + line.length;

    if (projectedLength > PREVIEW_MAX_CHARS) {
      const remaining = PREVIEW_MAX_CHARS - result.length - prefix.length;
      if (remaining > 0) result += prefix + line.slice(0, remaining);
      linesUsed++;
      break;
    }

    result += prefix + line;
    linesUsed++;
  }

  result = result.replace(/[\s\n]+$/g, '');
  if (result.length < body.length) return `${result}…`;
  return result;
}

function isBodyLong(post) {
  if (hasTextBackground(post)) return false;
  return bodyPreviewText(post).length < getPostBody(post).length;
}

function displayBodyText(post) {
  const body = getPostBody(post);
  if (hasTextBackground(post)) return body;
  if (!isBodyLong(post) || isPostExpanded(post)) return body;
  return bodyPreviewText(post);
}

function escapeHtml(raw) {
  return String(raw || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function linkifyText(raw) {
  const escaped = escapeHtml(raw);
  const urlRegex = /((https?:\/\/|www\.)[^\s<]+)/gi;

  return escaped.replace(urlRegex, (fullMatch) => {
    let match = fullMatch;
    let trailing = '';
    while (/[),.!?:;]+$/.test(match)) {
      trailing = match.slice(-1) + trailing;
      match = match.slice(0, -1);
    }

    const href = match.startsWith('www.') ? `https://${match}` : match;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-[#2D8CFF] font-medium hover:underline break-all">${match}</a>${trailing}`;
  });
}

function displayBodyHtml(post) {
  return linkifyText(displayBodyText(post));
}

function backgroundTextToneClass(post) {
  const key = getPostIdKey(post);
  const tone = String(textBackgroundToneByPostId.value[key] || 'dark');
  return tone === 'light'
    ? 'text-[#1f2937] font-semibold drop-shadow-[0_1px_8px_rgba(255,255,255,0.35)]'
    : 'text-white font-semibold drop-shadow-[0_1px_8px_rgba(0,0,0,0.45)]';
}

function backgroundTextButtonClass(post) {
  const key = getPostIdKey(post);
  const tone = String(textBackgroundToneByPostId.value[key] || 'dark');
  return tone === 'light' ? 'text-[#1f2937]' : 'text-white';
}

async function refreshBackgroundTones(list) {
  const next = { ...textBackgroundToneByPostId.value };
  const posts = Array.isArray(list) ? list : [];

  for (const post of posts) {
    const idKey = getPostIdKey(post);
    if (!idKey || !hasTextBackground(post)) continue;
    const value = String(post?.backgroundColor || post?.background_color || '').trim();
    const mapped = getNewsfeedBackgroundById(value);
    const url = mapped?.backgroundUrl || '';
    if (!url) continue;
    next[idKey] = guessNewsfeedBackgroundTone(url);
  }
  textBackgroundToneByPostId.value = next;

  for (const post of posts) {
    const idKey = getPostIdKey(post);
    if (!idKey || !hasTextBackground(post)) continue;
    const value = String(post?.backgroundColor || post?.background_color || '').trim();
    const mapped = getNewsfeedBackgroundById(value);
    const url = mapped?.backgroundUrl || '';
    if (!url) continue;
    const detected = await detectNewsfeedBackgroundTone(url);
    textBackgroundToneByPostId.value = {
      ...textBackgroundToneByPostId.value,
      [idKey]: detected,
    };
  }
}

function resolvePendingFeatureInstanceId(post) {
  const raw =
    post?.featureInstanceId ??
    post?.instanceId ??
    post?.instance_id ??
    props.featureInstanceId ??
    null;
  const normalized = String(raw || '').trim();
  return normalized || '';
}

function buildImageUrl(fileId, variant = '', post = null) {
  const id = String(fileId || '').trim();
  if (!id) return '';
  const params = new URLSearchParams();
  const featureInstanceId = resolvePendingFeatureInstanceId(post);
  if (featureInstanceId) params.set('featureInstanceId', featureInstanceId);
  if (variant) params.set('variant', String(variant).trim());
  const q = params.toString();
  return `${apiProxyUrl}/api/creator-newsfeed/image/${encodeURIComponent(id)}${q ? `?${q}` : ''}`;
}

function buildPlaylistUrl(fileId, post = null) {
  const id = String(fileId || '').trim();
  if (!id) return '';
  const params = new URLSearchParams();
  const featureInstanceId = resolvePendingFeatureInstanceId(post);
  if (featureInstanceId) params.set('featureInstanceId', featureInstanceId);
  const q = params.toString();
  return `${apiProxyUrl}/api/creator-newsfeed/stream-hls/playlist/${encodeURIComponent(id)}${q ? `?${q}` : ''}`;
}

const imageFileExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.avif'];
const videoFileExtensions = ['.mp4', '.mov', '.m4v', '.webm', '.mkv', '.avi', '.ogv'];

function inferAttachmentKind(att) {
  const mime = String(att?.mimeType || att?.mime || att?.contentType || '').toLowerCase().trim();
  const fileType = String(att?.fileType || att?.type || '').toLowerCase().trim();
  const url = String(
    att?.url || att?.secureVideoUrl || att?.videoUrl || att?.secureImageUrl || att?.imageUrl || ''
  ).toLowerCase();

  if (mime.startsWith('video/') || fileType === 'video') return 'video';
  if (mime.startsWith('image/') || fileType === 'image') return 'image';
  if (videoFileExtensions.some((ext) => url.includes(ext))) return 'video';
  if (imageFileExtensions.some((ext) => url.includes(ext))) return 'image';
  if (att?.videoUrl || att?.secureVideoUrl || att?.thumbnailUrl || att?.posterUrl) return 'video';
  return 'image';
}

function getDisplayMedia(post) {
  const attachments = Array.isArray(post?.attachments) ? post.attachments : [];
  const normalized = attachments
    .map((att) => {
      if (!att) return null;
      const fileId = String(att.fileId || att.file_id || att.mediaFileId || '').trim();
      const kind = inferAttachmentKind(att);

      const imageSrc =
        (fileId ? buildImageUrl(fileId, '', post) : '') ||
        String(att.imageUrl || att.secureImageUrl || att.url || att.fileUrl || att.file_url || '').trim();
      const videoSrc =
        String(att.secureVideoUrl || att.videoUrl || att.url || att.hlsPlaylistUrl || att.hls_playlist_url || '').trim() ||
        (fileId ? buildPlaylistUrl(fileId, post) : '');
      const thumbnailSrc =
        String(att.thumbnailUrl || att.thumbnail_url || att.posterUrl || '').trim() ||
        (fileId ? buildImageUrl(fileId, 'thumbnail', post) : '') ||
        imageSrc;

      if (kind === 'video') {
        const resolvedVideoSrc = videoSrc || imageSrc;
        if (!resolvedVideoSrc) return null;
        return {
          type: 'video',
          fileId: fileId || null,
          videoSrc: resolvedVideoSrc,
          thumbnailSrc: thumbnailSrc || '',
        };
      }

      if (!imageSrc) return null;
      return {
        type: 'image',
        fileId: fileId || null,
        src: imageSrc,
      };
    })
    .filter(Boolean);

  if (normalized.length) return normalized.slice(0, 5);

  const coverMediaFileId = String(post?.coverMediaFileId || post?.cover_media_file_id || '').trim();
  if (coverMediaFileId) {
    return [
      {
        type: 'image',
        fileId: coverMediaFileId,
        src: buildImageUrl(coverMediaFileId, '', post),
      },
    ];
  }

  return [];
}

async function fetchPendingPosts() {
  const instanceId = featureInstanceIdNum.value;
  if (!instanceId) {
    pendingPosts.value = [];
    return;
  }

  loading.value = true;
  errorMessage.value = '';

  try {
    const params = new URLSearchParams({
      featureInstanceId: String(instanceId),
      limit: '100',
      offset: '0',
      includePending: '1',
    });

    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      errorMessage.value = data?.error || 'There was a problem loading pending posts.';
      pendingPosts.value = [];
      return;
    }

    const list = Array.isArray(data?.posts) ? data.posts : [];
    pendingPosts.value = sortPending(
      list.filter((p) => {
        const status = String(p?.status || p?.postStatus || '').toLowerCase();
        const authorType = String(p?.authorUserType || p?.author_user_type || '').toLowerCase();
        return status === 'pending_approval' && authorType === 'member';
      })
    );
    void refreshBackgroundTones(pendingPosts.value);
  } catch (err) {
    console.error('[NewsfeedCreatorPendingTab] fetchPendingPosts error:', err);
    errorMessage.value = 'Network error while loading pending posts.';
    pendingPosts.value = [];
  } finally {
    loading.value = false;
  }
}

async function approvePost(post) {
  const postId = Number(post?.id);
  if (!Number.isFinite(postId) || postId <= 0) return;
  if (actioningPostId.value) return;

  actioningPostId.value = postId;
  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(postId)}/approve`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      throw new Error(data?.error || `Approve failed (${res.status})`);
    }

    pendingPosts.value = pendingPosts.value.filter((p) => String(p.id) !== String(postId));

    if (typeof notify === 'function') {
      notify({
        title: 'Post approved',
        body: 'You approved a member post. It is now live in Discussion.',
        variant: 'success',
        duration: 10000,
      });
    }
  } catch (err) {
    console.error('[NewsfeedCreatorPendingTab] approvePost error:', err);
    if (typeof notify === 'function') {
      notify({
        title: 'Approval failed',
        body: 'Could not approve this pending post.',
        variant: 'error',
        duration: 10000,
      });
    }
  } finally {
    actioningPostId.value = null;
  }
}

function openRejectModal(post) {
  const postId = Number(post?.id);
  if (!Number.isFinite(postId) || postId <= 0) return;
  rejectTargetPostId.value = postId;
  rejectModeActive.value = false;
  rejectModalOpen.value = true;
}

function closeRejectModal(force = false) {
  if (!force && actioningPostId.value === rejectTargetPostId.value) return;
  rejectModalOpen.value = false;
  rejectTargetPostId.value = null;
  rejectModeActive.value = false;
}

async function confirmReject() {
  const postId = Number(rejectTargetPostId.value);
  if (!Number.isFinite(postId) || postId <= 0) return;
  if (actioningPostId.value) return;

  actioningPostId.value = postId;
  rejectModeActive.value = true;
  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(postId)}/reject`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) {
      throw new Error(data?.error || `Reject failed (${res.status})`);
    }

    pendingPosts.value = pendingPosts.value.filter((p) => String(p.id) !== String(postId));
    closeRejectModal(true);

    if (typeof notify === 'function') {
      notify({
        title: 'Post rejected',
        body: 'The pending post was rejected and removed.',
        variant: 'success',
        duration: 10000,
      });
    }
  } catch (err) {
    console.error('[NewsfeedCreatorPendingTab] rejectPost error:', err);
    if (typeof notify === 'function') {
      notify({
        title: 'Reject failed',
        body: 'Could not reject this pending post.',
        variant: 'error',
        duration: 10000,
      });
    }
  } finally {
    actioningPostId.value = null;
    rejectModeActive.value = false;
  }
}

function subscribeToLivePending() {
  if (unsubscribeNewsfeedPostCreated) return;
  unsubscribeNewsfeedPostCreated = websocketStore.subscribe('newsfeed:postCreated', (payload) => {
    if (!payload) return;
    const incomingInstanceId = Number(payload.instanceId);
    if (!incomingInstanceId || incomingInstanceId !== featureInstanceIdNum.value) return;

    const status = String(payload.status || '').toLowerCase();
    const authorUserType = String(payload.authorUserType || '').toLowerCase();
    if (status !== 'pending_approval' || authorUserType !== 'member') return;

    void fetchPendingPosts();
  });
}

function handlePendingBadgeTooltipEnter(postId, event) {
  const key = String(postId || '');
  if (typeof window === 'undefined' || !key || !event?.currentTarget) return;

  const triggerEl = event.currentTarget;
  const rect = triggerEl.getBoundingClientRect();
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;

  // Keep this aligned with the creator post tooltip decision model.
  const estimatedTooltipHeight = 380;

  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  // If there isn't enough room below and there's more room above, flip it.
  const showAbove =
    spaceBelow < estimatedTooltipHeight && spaceAbove > spaceBelow;

  pendingBadgeTooltipPosition.value = {
    ...pendingBadgeTooltipPosition.value,
    [key]: showAbove ? 'above' : 'below',
  };
}

function unsubscribeLivePending() {
  if (!unsubscribeNewsfeedPostCreated) return;
  unsubscribeNewsfeedPostCreated();
  unsubscribeNewsfeedPostCreated = null;
}

defineExpose({
  reloadPosts: fetchPendingPosts,
});

onMounted(() => {
  void fetchPendingPosts();
  subscribeToLivePending();
});

onBeforeUnmount(() => {
  unsubscribeLivePending();
});

watch(
  () => featureInstanceIdNum.value,
  () => {
    void fetchPendingPosts();
  }
);
</script>

